import {
  METRIC_UNIT,
  METRIC_UNIT_BYTES_LEGACY,
} from "src/business-utils/metrics/metricUnits.constants";
import { formatBytes } from "./formatBytes";

/**
 * Format CPU time into human-readable format (ns, μs, ms, s, min, h)
 * @param timeMs Time in milliseconds to format
 * @param decimals Number of decimal places to show (default: 2)
 */
export const formatCpuTime = (timeMs: number, { decimals = 2 } = {}): string => {
  if (timeMs === 0) return "0";
  if (isNaN(timeMs) || timeMs === undefined) return "";

  // Define time units in milliseconds
  const units = [
    { value: 0.000001, label: "ns" }, // nanoseconds
    { value: 0.001, label: "μs" }, // microseconds
    { value: 1, label: "ms" }, // milliseconds
    { value: 1000, label: "s" }, // seconds
    { value: 60000, label: "min" }, // minutes
    { value: 3600000, label: "h" }, // hours
  ];

  // Find the appropriate unit
  let unitIndex = 0;
  for (let i = 0; i < units.length; i++) {
    if (i === units.length - 1 || timeMs < units[i + 1].value) {
      unitIndex = i;
      break;
    }
  }

  const unit = units[unitIndex];

  // Special case for nanoseconds (values smaller than 1 microsecond)
  if (unitIndex === 0) {
    return `${Math.round(timeMs / unit.value)} ${unit.label}`;
  }

  // For all other units
  const value = timeMs / unit.value;
  return `${parseFloat(value.toFixed(decimals))} ${unit.label}`;
};

export const formatMetricValue = (
  units: string | undefined,
  options: { binary?: boolean } = {}
): undefined | ((d: number) => string) => {
  if (!units) return;
  switch (units) {
    case METRIC_UNIT.BYTES:
    /** Legacy payload spelling distinct from lowercase `METRIC_UNIT.BYTES`. */
    case METRIC_UNIT_BYTES_LEGACY:
      return (d: number) => formatBytes(d, { binary: options.binary ?? true });
    case METRIC_UNIT.MS:
      return formatCpuTime;
    case METRIC_UNIT.SECONDS:
      return (d: number) => formatCpuTime(d * 1000);
    default:
      return;
  }
};
